import React from 'react';
import { useHistory } from 'react-router';

import { facilitiesURL, facilityURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  FacilitiesListDocument,
  FacilityUpdateInput,
  useFacilitiesListQuery,
  useFacilityCreateMutation,
} from '@admin/schema';
import { BreadcrumbCurrent } from '@admin/styled/breadcrumb_current';
import { BreadcrumbNav } from '@admin/styled/breadcrumb_nav';
import { BreadcrumbSep } from '@admin/styled/breadcrumb_sep';
import { ButtonLink } from '@admin/styled/button_link';
import { ErrorBanner } from '@admin/styled/error_banner';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageTitle } from '@admin/styled/page_title';

import { FacilityForm } from './facility_form';
import { emptyFacilityFormInput } from './facility_update_input';

export const CreateFacility: React.FC = () => {
  useTitle('New Facility');
  const history = useHistory();
  const [error, setError] = React.useState<string | undefined>();
  const { data, loading } = useFacilitiesListQuery({ client, variables: { includeInactive: true } });
  const [createFacility, { loading: saving }] = useFacilityCreateMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [{ query: FacilitiesListDocument, variables: { includeInactive: true } }],
  });

  const onSubmit = async (input: FacilityUpdateInput) => {
    setError(undefined);
    const result = await createFacility({ variables: { input } });
    const payload = result.data?.facilityCreate;
    if (!payload) {
      setError('Could not create facility.');
      return;
    }
    if (payload.error) {
      setError(payload.error);
      return;
    }
    if (payload.facility?.id) {
      history.push(facilityURL({ id: payload.facility.id }));
    }
  };

  if (loading && !data) {
    return null;
  }

  const allServiceTypes = data?.facilityServiceTypes ?? [];

  return (
    <Page>
      <BreadcrumbNav>
        <ButtonLink to={facilitiesURL()}>Facilities</ButtonLink>
        <BreadcrumbSep />
        <BreadcrumbCurrent>New facility</BreadcrumbCurrent>
      </BreadcrumbNav>
      <PageHeader>
        <PageTitle>New facility</PageTitle>
      </PageHeader>
      {error && <ErrorBanner role="alert">{error}</ErrorBanner>}
      <FacilityForm
        initialValues={emptyFacilityFormInput()}
        allServiceTypes={allServiceTypes}
        onSubmit={onSubmit}
        onCancel={() => history.push(facilitiesURL())}
        saving={saving}
      />
    </Page>
  );
};
